import React, { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  FolderIcon,
  LockClosedIcon,
  MinusCircleIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import SlideOver from "../../../components/modals/SlideOver/SlideOver";
import PassagePanel from "../../../components/Passages/PassagePanel";
import { PreviewOption, RichText } from "../../../components/QuestionFolders/ImportPreviewQuestion";
import { getQuestionByUUID } from "../../../services/questionService";
import { folderPath } from "../../../utils/folders";
import { isCanceledError } from "../../../utils/examError";
import { AddPassageButton } from "./QuestionBrowser";
import { answersHidden, cardClass, focusRing, iconButtonClass, isTestBlocked, linkButtonClass, Spinner, StatusBadge } from "./ui";

// Full questions (with body and answer keys) already fetched, least recently used first.
const cache = new Map();
const remember = (question) => {
  cache.delete(question.uuid);
  cache.set(question.uuid, question);
  if (cache.size > 50) cache.delete(cache.keys().next().value);
};

// Bank rows come without body and answer keys; test rows already have them.
function useFullQuestion(question) {
  const [state, setState] = useState({ data: null, loading: false, error: null });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!question) {
      setState({ data: null, loading: false, error: null });
      return undefined;
    }
    if (question.body !== undefined) {
      setState({ data: question, loading: false, error: null });
      return undefined;
    }
    const cached = cache.get(question.uuid);
    if (cached) {
      remember(cached);
      setState({ data: cached, loading: false, error: null });
      return undefined;
    }
    const controller = new AbortController();
    setState({ data: null, loading: true, error: null });
    getQuestionByUUID({ uuid: question.uuid, signal: controller.signal })
      .then((data) => {
        remember(data);
        setState({ data, loading: false, error: null });
      })
      .catch((err) => {
        if (!isCanceledError(err)) setState({ data: null, loading: false, error: err });
      });
    return () => controller.abort();
  }, [question, attempt]);
  return { ...state, retry: () => setAttempt((n) => n + 1) };
}

function MetaRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="break-words text-sm text-slate-800">{value}</dd>
    </div>
  );
}

// `inTestIds` and `onAddQuestions` add the rest of the question's passage ("Add whole passage"). `blocked`:
// the exam does not take it into tests yet, said in the note `noteId` (the add button points to it).
function PreviewBody({ question, full, inTest, blocked, noteId, inTestIds, blockedIds, onAddQuestions }) {
  const [t] = useTranslation();
  const q = full.data || question;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-1.5">
        {q.code && <span className="font-mono text-xs text-slate-500">{q.code}</span>}
        <StatusBadge status={q.status} />
        {q.subject?.name && !q.folder && (
          <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
            {q.subject.name}
          </span>
        )}
        {q.folder?.name && (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-500/20">
            <FolderIcon className="h-3 w-3" aria-hidden="true" />
            {folderPath(q.folder)}
          </span>
        )}
      </div>
      {blocked && (
        <p id={noteId} className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <LockClosedIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
          {inTest ? t("test_bank.builder.blocked_row") : t("test_bank.builder.blocked_reason")}
        </p>
      )}
      {/* Keyed by the passage: stepping through its questions keeps the text open. */}
      {q.passage?.uuid && (
        <PassagePanel
          key={q.passage.uuid}
          passage={q.passage}
          actions={
            inTestIds && onAddQuestions
              ? (detail) => <AddPassageButton detail={detail} inTest={inTestIds} onAdd={onAddQuestions} blockedIds={blockedIds} />
              : undefined
          }
        />
      )}
      {full.loading ? (
        <div className="space-y-2" aria-hidden="true">
          <span className="block h-24 rounded-xl bg-slate-100 motion-safe:animate-pulse" />
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className="block h-9 rounded-lg bg-slate-100 motion-safe:animate-pulse" />
          ))}
        </div>
      ) : full.error ? (
        <div role="alert" className="flex flex-wrap items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t("test_bank.builder.preview_error")}
          <button type="button" onClick={full.retry} className={`${linkButtonClass} ml-auto`}>
            {t("test_bank.builder.retry")}
          </button>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-sm leading-relaxed text-slate-800 [&_img]:max-h-80 [&_img]:w-auto">
            <RichText html={q.body} />
          </div>
          {q.answer_keys?.length > 0 && (
            <ul className="space-y-1.5">
              {q.answer_keys.map((option, i) => (
                <PreviewOption key={option.uuid || option.option || i} option={option} />
              ))}
            </ul>
          )}
          {/* A test's question whose answers the user may not read: no option is marked right (is_correct null). */}
          {answersHidden(q) && (
            <p className="flex items-start gap-2 text-sm text-slate-500" data-testid="preview-answers-hidden">
              <EyeSlashIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
              <span className="min-w-0">{t("test_bank.builder.answers_hidden_note")}</span>
            </p>
          )}
        </>
      )}
      <dl className="grid grid-cols-1 gap-3 border-t border-slate-100 pt-3 sm:grid-cols-2">
        <MetaRow label={t("test_bank.builder.meta_topic")} value={q.subtopic?.topic?.name} />
        <MetaRow label={t("test_bank.builder.meta_subtopic")} value={q.subtopic?.name} />
        <MetaRow label={t("test_bank.builder.meta_term")} value={q.purpose?.name} />
        <MetaRow label={t("test_bank.builder.meta_objective")} value={q.objective?.name} />
      </dl>
    </div>
  );
}

// `blocked`: the question can't be added (the note `noteId` says why); removing it asks first.
function PreviewActions({ inTest, pending, blocked, noteId, onAdd, onRemove, onPrev, onNext }) {
  const [t] = useTranslation();
  const locked = blocked && !inTest && !pending;
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onPrev}
          disabled={!onPrev}
          aria-label={t("test_bank.builder.prev_question")}
          className={`${iconButtonClass} max-sm:h-11 max-sm:w-11`}
        >
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!onNext}
          aria-label={t("test_bank.builder.next_question")}
          className={`${iconButtonClass} max-sm:h-11 max-sm:w-11`}
        >
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      {/* One button for every state (focus stays on it); a double click must not undo the first click. */}
      <button
        type="button"
        onClick={(e) => {
          if (pending || locked || e.detail > 1) return;
          if (inTest) onRemove();
          else onAdd();
        }}
        aria-disabled={pending || locked || undefined}
        aria-describedby={locked ? noteId : undefined}
        className={`inline-flex h-11 items-center gap-1.5 rounded-lg px-4 text-sm font-medium transition duration-200 sm:h-10 ${focusRing} ${
          pending
            ? "cursor-wait bg-white text-slate-500 ring-1 ring-inset ring-slate-200"
            : locked
            ? "cursor-not-allowed bg-slate-100 text-slate-500 ring-1 ring-inset ring-slate-200"
            : inTest
            ? "bg-white text-red-600 shadow-sm ring-1 ring-inset ring-red-200 hover:bg-red-50"
            : "bg-blue-600 text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700"
        }`}
      >
        {pending ? (
          <>
            <Spinner />
            {t("test_bank.builder.saving")}
          </>
        ) : locked ? (
          <>
            <LockClosedIcon className="h-4 w-4" aria-hidden="true" />
            {t("test_bank.builder.cannot_add")}
          </>
        ) : inTest ? (
          <>
            <MinusCircleIcon className="h-4 w-4" aria-hidden="true" />
            {t("test_bank.builder.remove_from_test")}
          </>
        ) : (
          <>
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
            {t("test_bank.builder.add_to_test")}
          </>
        )}
      </button>
    </div>
  );
}

// The question's name and the way to its page in the bank (not for a question whose answers the user may not read:
// they do not read it there either).
function PreviewTitle({ question }) {
  const [t] = useTranslation();
  return (
    <div className="flex min-w-0 items-start gap-2">
      <h2 id="builder-preview-title" className="min-w-0 flex-1 break-words text-base font-semibold text-slate-900">
        {question?.name || question?.code || t("test_bank.builder.preview_title")}
      </h2>
      {question?.uuid && !answersHidden(question) && (
        <a
          href={`/questions/${question.uuid}`}
          target="_blank"
          rel="noopener noreferrer"
          title={t("test_bank.builder.open_full")}
          aria-label={t("test_bank.builder.open_full")}
          className={`${iconButtonClass} -mt-1 h-8 w-8 max-sm:-my-1.5 max-sm:h-11 max-sm:w-11`}
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
        </a>
      )}
    </div>
  );
}

/**
 * The question being looked at, without leaving the builder: an inline column on very wide screens
 * (layout "panel") or a panel sliding in from the right (layout "sheet"). A question on a passage shows
 * the passage (collapsed) with "Add whole passage": onAddQuestions(questions) with those not in
 * `inTestIds`. A question its exam does not take into tests yet (isTestBlocked, or in `blockedIds`: refused
 * by the server) can't be added, and says why.
 * `children` of the sheet are dialogs asked from it: rendered inside it, they are nested in its dialog, so
 * Escape and clicks outside them close them and not the sheet under them.
 */
export default function QuestionPreview({
  layout,
  open,
  question,
  inTest,
  pending,
  inTestIds,
  blockedIds,
  onAdd,
  onAddQuestions,
  onRemove,
  onPrev,
  onNext,
  onClose,
  children,
}) {
  const [t] = useTranslation();
  const noteId = useId();
  const full = useFullQuestion(open ? question : null);
  // As its row says (the full question may come from the cache, older than the list).
  const blocked = !!question && (isTestBlocked(question) || !!blockedIds?.has(question.uuid));
  const actions = question && (
    <PreviewActions
      inTest={inTest}
      pending={pending}
      blocked={blocked}
      noteId={noteId}
      onAdd={onAdd}
      onRemove={onRemove}
      onPrev={onPrev}
      onNext={onNext}
    />
  );
  const body = question && (
    <PreviewBody
      question={question}
      full={full}
      inTest={inTest}
      blocked={blocked}
      noteId={noteId}
      inTestIds={inTestIds}
      blockedIds={blockedIds}
      onAddQuestions={onAddQuestions}
    />
  );

  if (layout === "sheet") {
    return (
      <SlideOver
        isOpen={open}
        onClose={onClose}
        title={<PreviewTitle question={question} />}
        footer={actions}
        closeLabel={t("buttons.close")}
        widthClass="max-w-2xl"
      >
        {body}
        {children}
      </SlideOver>
    );
  }

  return (
    <section aria-labelledby="builder-preview-title" className={`${cardClass} flex min-h-0 flex-col xl:h-full`}>
      <div className="border-b border-slate-100 px-4 py-3">
        <PreviewTitle question={question} />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {question ? (
          body
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 py-10 text-center">
            <EyeIcon className="h-8 w-8 text-slate-300" aria-hidden="true" />
            <p className="max-w-xs text-sm text-slate-500">{t("test_bank.builder.preview_empty")}</p>
          </div>
        )}
      </div>
      {actions && <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-3">{actions}</div>}
    </section>
  );
}
